import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell from "../components/Shell";
import LeadFormDialog from "../components/LeadFormDialog";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { statusClass, toDDMMYYYY, toDateTime, LOSS_REASONS, lossReasonLabel, PHOTO_TAGS } from "../lib/helpers";
import { compressImage } from "../lib/image";
import { log } from "../lib/logger";
import { ArrowLeft, Camera, Edit, Trash2, ExternalLink, X, Plus, MapPin, CalendarPlus, Check, RotateCcw, Pencil, Navigation, Loader2, AlertTriangle, MessageCircle } from "lucide-react";
import WhatsAppShareDialog from "../components/WhatsAppShareDialog";

const CONTACT_ROLES = ["owner", "architect", "engineer", "civil_contractor", "site_incharge", "other"];

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [users, setUsers] = useState([]);
  const [zoomPhoto, setZoomPhoto] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpNotes, setFollowUpNotes] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [newContact, setNewContact] = useState({ role: "owner", name: "", phone: "" });
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);
  const [checkInBusy, setCheckInBusy] = useState(false);
  const [lossPrompt, setLossPrompt] = useState(null); // { reason, note, saving, error }
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(null); // {done, total} or null
  // Lazy photo cache: { [photoId]: base64DataUrl }. Photos load on demand instead
  // of shipping every image with the lead, so the detail page opens instantly.
  const [photoCache, setPhotoCache] = useState({});
  const [photoSelectMode, setPhotoSelectMode] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState(() => new Set());
  const onPhotoLoaded = useCallback((photoId, data) => {
    setPhotoCache((c) => (c[photoId] ? c : { ...c, [photoId]: data }));
  }, []);

  const togglePhotoSelect = useCallback((photoId) => {
    setSelectedPhotoIds((prev) => {
      const n = new Set(prev);
      if (n.has(photoId)) n.delete(photoId); else n.add(photoId);
      return n;
    });
  }, []);

  const exitPhotoSelect = () => { setPhotoSelectMode(false); setSelectedPhotoIds(new Set()); };

  const onDeleteSelectedPhotos = async () => {
    const ids = Array.from(selectedPhotoIds);
    if (!ids.length) return;
    if (!confirm(`Delete ${ids.length} selected photo${ids.length > 1 ? "s" : ""}? This can't be undone.`)) return;
    const r = await api.post(`/leads/${id}/photos/delete?light=1`, { photo_ids: ids });
    setLead(r.data);
    setPhotoCache((c) => { const n = { ...c }; ids.forEach((pid) => delete n[pid]); return n; });
    exitPhotoSelect();
  };

  const captureGps = useCallback(() => new Promise((resolve) => {
    if (!navigator.geolocation) { resolve(null); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
    );
  }), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [a, b] = await Promise.all([api.get(`/leads/${id}?light=1`), api.get("/users")]);
      if (cancelled) return;
      setLead(a.data);
      setUsers(b.data);
    })();
    return () => { cancelled = true; };
  }, [id, reloadTick]);

  if (!lead) {
    return <Shell><div className="px-8 py-7">Loading…</div></Shell>;
  }

  const onAddPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    setUploadingPhotos({ done: 0, total: files.length });
    const failures = [];
    let latest = lead;
    // Upload sequentially so the backend `photos[]` array grows in the order the user picked,
    // and so we can update UI progress + surface partial failures without cancelling the batch.
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const data = await compressImage(f, { maxDim: 1600, quality: 0.75 });
        const r = await api.post(`/leads/${id}/photos?light=1`, { data });
        latest = r.data;
        // Seed the cache with the freshly-uploaded image (newest photo is last) so it
        // shows immediately without a round-trip back to the server.
        const newPhoto = latest.photos?.[latest.photos.length - 1];
        if (newPhoto) onPhotoLoaded(newPhoto.id, data);
        setLead(latest);
      } catch (ex) {
        failures.push(`${f.name}: ${ex?.response?.data?.detail || ex.message || "failed"}`);
      }
      setUploadingPhotos({ done: i + 1, total: files.length });
    }
    setUploadingPhotos(null);
    if (failures.length) {
      alert(`Uploaded ${files.length - failures.length} of ${files.length} photo(s).\n\nSkipped:\n${failures.join("\n")}`);
    }
  };

  const onDeletePhoto = async (photoId) => {
    if (!confirm("Delete this photo?")) return;
    const r = await api.delete(`/leads/${id}/photos/${photoId}?light=1`);
    setLead(r.data);
    setPhotoCache((c) => { const n = { ...c }; delete n[photoId]; return n; });
    setZoomPhoto(null);
  };

  const onSaveCaption = async (photoId, caption) => {
    const r = await api.patch(`/leads/${id}/photos/${photoId}?light=1`, { caption });
    setLead(r.data);
    setZoomPhoto((z) => (z && z.id === photoId ? { ...z, caption } : z));
  };

  const onAddFollowUp = async () => {
    if (!followUpNotes && !followUpDate) return;
    // Follow-ups are call/admin work and do NOT carry GPS for travel purposes — use "I'm here" for site visits.
    const r = await api.post(`/leads/${id}/follow-ups`, {
      notes: followUpNotes,
      next_visit_date: followUpDate ? new Date(followUpDate).toISOString() : null,
    });
    setLead(r.data);
    setFollowUpNotes("");
    setFollowUpDate("");
    setShowFollowUp(false);
  };

  const onCheckIn = async () => {
    setCheckInBusy(true);
    try {
      const g = await captureGps();
      if (!g) {
        alert("Couldn't capture your location. Please enable GPS / location permission and retry.");
        return;
      }
      const r = await api.post(`/leads/${id}/visits`, {
        latitude: g.lat,
        longitude: g.lng,
        accuracy_m: g.acc,
        note: "",
      });
      setLead(r.data);
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Check-in failed");
    } finally {
      setCheckInBusy(false);
    }
  };

  const onDeleteVisit = async (visit) => {
    if (!confirm("Delete this check-in?")) return;
    try {
      const r = await api.delete(`/leads/${id}/visits/${visit.id}`);
      setLead(r.data);
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Delete failed");
    }
  };

  const onToggleFollowUpComplete = async (fu) => {
    const r = await api.patch(`/leads/${id}/follow-ups/${fu.id}`, { completed: !fu.completed });
    setLead(r.data);
  };

  const onDeleteFollowUp = async (fu) => {
    if (!confirm("Delete this follow-up?")) return;
    const r = await api.delete(`/leads/${id}/follow-ups/${fu.id}`);
    setLead(r.data);
  };

  const onSaveEditFollowUp = async (fu, notes, dateISO) => {
    const r = await api.patch(`/leads/${id}/follow-ups/${fu.id}`, {
      notes: notes,
      next_visit_date: dateISO || null,
    });
    setLead(r.data);
  };

  const onAddContact = async () => {
    if (!newContact.name) return;
    const r = await api.patch(`/leads/${id}`, {
      contacts: [...(lead.contacts || []), newContact],
    });
    setLead(r.data);
    setNewContact({ role: "owner", name: "", phone: "" });
  };

  const onRemoveContact = async (idx) => {
    const newContacts = (lead.contacts || []).filter((_, i) => i !== idx);
    const r = await api.patch(`/leads/${id}`, { contacts: newContacts });
    setLead(r.data);
  };

  const onDelete = async () => {
    if (!confirm("Delete this lead permanently?")) return;
    await api.delete(`/leads/${id}`);
    navigate("/leads");
  };

  const onStatusChange = async (status) => {
    // Moving to LOST always requires a reason + note.
    if (status === "LOST") {
      setLossPrompt({
        reason: lead.loss_reason || "",
        note: lead.loss_note || "",
        saving: false,
        error: "",
      });
      return;
    }
    const r = await api.patch(`/leads/${id}`, { status });
    setLead(r.data);
  };

  const submitLossPrompt = async () => {
    const reason = (lossPrompt?.reason || "").trim();
    const note = (lossPrompt?.note || "").trim();
    if (!reason) {
      setLossPrompt((p) => ({ ...p, error: "Please pick a reason for the loss." }));
      return;
    }
    if (!note) {
      setLossPrompt((p) => ({ ...p, error: "Please add a detailed note explaining the loss." }));
      return;
    }
    setLossPrompt((p) => ({ ...p, saving: true, error: "" }));
    try {
      const r = await api.patch(`/leads/${id}`, {
        status: "LOST",
        loss_reason: reason,
        loss_note: note,
      });
      setLead(r.data);
      setLossPrompt(null);
    } catch (ex) {
      setLossPrompt((p) => ({
        ...p,
        saving: false,
        error: ex?.response?.data?.detail || ex.message || "Failed to mark as lost.",
      }));
    }
  };

  const photos = [...(lead.photos || [])].sort((a, b) => new Date(b.added_at) - new Date(a.added_at));
  // Open follow-ups first (by newest), then completed ones (by newest completion)
  const followUps = [...(lead.follow_ups || [])].sort((a, b) => {
    if (!!a.completed !== !!b.completed) return a.completed ? 1 : -1;
    return new Date(b.created_at) - new Date(a.created_at);
  });
  const openFollowUpCount = (lead.follow_ups || []).filter((f) => !f.completed).length;
  const visits = [...(lead.visits || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const assignee = users.find((u) => u.id === lead.assigned_to);

  return (
    <Shell>
      <div className="px-4 sm:px-8 py-5 sm:py-6 max-w-[1300px]">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="font-mono text-sm text-slate-500">{lead.lead_no}</div>
            <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>{lead.building_name || "Unnamed"}</h1>
            <div className="flex items-center gap-3 mt-2">
              <span className={statusClass(lead.status)} data-testid="lead-status">{(lead.status || "NEW").replace("_", " ")}</span>
              <span className="text-sm text-slate-500">{lead.city || ""}{lead.locality ? ` · ${lead.locality}` : ""}</span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={onCheckIn} disabled={checkInBusy} className="sohom-btn-primary" data-testid="checkin-btn" title="Capture your current GPS as a site visit">
              {checkInBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
              {checkInBusy ? "Checking in…" : "I'm here"}
            </button>
            <button
              onClick={() => setShowWhatsApp(true)}
              className="sohom-btn-secondary text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              data-testid="whatsapp-share-btn"
              title="Send a prefilled intro message on WhatsApp"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </button>
            <select className="sohom-input w-40" value={lead.status} onChange={(e) => onStatusChange(e.target.value)} data-testid="quick-status">
              {["NEW", "CONTACTED", "IN_PROGRESS", "WON", "LOST"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
            <button onClick={() => setShowEdit(true)} className="sohom-btn-secondary" data-testid="edit-lead-btn"><Edit className="w-4 h-4" /> Edit</button>
            {user?.role !== "field_executive" && (
              <button onClick={onDelete} className="sohom-btn-secondary text-red-600 hover:bg-red-50" data-testid="delete-lead-btn"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left: details */}
          <div className="lg:col-span-2 space-y-5">
            <div className="sohom-card p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Site Details</h3>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <Detail label="Address" value={lead.address} full />
                <Detail label="Plot" value={lead.plot_no} />
                <Detail label="Street" value={lead.street} />
                <Detail label="Locality" value={lead.locality} />
                <Detail label="City" value={lead.city} />
                <Detail label="Pincode" value={lead.pincode} />
                <Detail label="State" value={lead.state} />
                <Detail label="Building Type" value={lead.building_type} />
                <Detail label="Construction Stage" value={lead.construction_stage} />
                <Detail label="Estimated Units" value={lead.estimated_units} />
                <Detail label="Assigned To" value={assignee?.full_name || "Unassigned"} />
                <Detail label="Created By" value={lead.created_by_name} />
                <Detail label="Created" value={toDateTime(lead.created_at)} />
                <Detail label="Updated" value={toDateTime(lead.updated_at)} />
                {lead.requirement_notes && <Detail label="Requirement Notes" value={lead.requirement_notes} full />}
                {lead.status === "LOST" && (
                  <>
                    <Detail label="Loss Reason" value={lossReasonLabel(lead.loss_reason)} />
                    <div className="col-span-2">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Loss Note</div>
                      <div className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap" data-testid="loss-note-display">
                        {lead.loss_note || <span className="text-slate-400">—</span>}
                      </div>
                    </div>
                  </>
                )}
                {lead.latitude && lead.longitude && (
                  <div className="col-span-2">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">GPS</div>
                    <a href={`https://www.google.com/maps?q=${lead.latitude},${lead.longitude}`} target="_blank" rel="noreferrer"
                       className="text-sky-600 hover:text-sky-700 font-medium inline-flex items-center gap-1 text-sm mt-1"
                       data-testid="gmaps-link">
                      <MapPin className="w-3.5 h-3.5" /> {lead.latitude.toFixed(5)}, {lead.longitude.toFixed(5)}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Contacts */}
            <div className="sohom-card p-5">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Contacts</h3>
              <div className="space-y-2 mb-3">
                {(lead.contacts || []).length === 0 ? (
                  <div className="text-sm text-slate-400 italic">No contacts added.</div>
                ) : (
                  (lead.contacts || []).map((c, i) => (
                    <div key={`${c.role}-${c.name}-${c.phone}-${i}`} className="flex items-center justify-between border border-slate-200 rounded-md px-3 py-2" data-testid={`contact-${i}`}>
                      <div>
                        <div className="font-medium text-slate-900 text-sm">{c.name} <span className="text-xs text-slate-500 ml-1">({c.role.replace("_"," ")})</span></div>
                        {c.phone && <a href={`tel:${c.phone}`} className="text-xs text-sky-600 hover:underline">{c.phone}</a>}
                      </div>
                      <button onClick={() => onRemoveContact(i)} className="p-1 hover:bg-slate-100 rounded text-slate-500"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))
                )}
              </div>
              <div className="flex gap-2 items-end pt-3 border-t border-slate-100">
                <div className="w-32">
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Role</label>
                  <select className="sohom-input mt-1" value={newContact.role} onChange={(e) => setNewContact({ ...newContact, role: e.target.value })} data-testid="contact-role">
                    {CONTACT_ROLES.map((r) => <option key={r} value={r}>{r.replace("_"," ")}</option>)}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Name</label>
                  <input className="sohom-input mt-1" value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} data-testid="contact-name" />
                </div>
                <div className="w-40">
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Phone</label>
                  <input className="sohom-input mt-1" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} data-testid="contact-phone" />
                </div>
                <button onClick={onAddContact} className="sohom-btn-primary mb-0.5" data-testid="add-contact-btn"><Plus className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Follow-ups */}
            <div className="sohom-card p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                  Follow-ups
                  {followUps.length > 0 && (
                    <span className="ml-2 text-xs font-normal text-slate-400 normal-case tracking-normal">
                      {openFollowUpCount} open · {followUps.length - openFollowUpCount} done
                    </span>
                  )}
                </h3>
                <button onClick={() => setShowFollowUp(!showFollowUp)} className="sohom-btn-secondary text-xs" data-testid="add-followup-btn">
                  <CalendarPlus className="w-3.5 h-3.5" /> {showFollowUp ? "Cancel" : "Add follow-up"}
                </button>
              </div>
              {showFollowUp && (
                <div className="border border-slate-200 rounded-lg p-3 mb-3 space-y-2 bg-slate-50">
                  <textarea
                    className="sohom-input"
                    rows={2}
                    placeholder="Notes from this visit / call…"
                    value={followUpNotes}
                    onChange={(e) => setFollowUpNotes(e.target.value)}
                    data-testid="followup-notes"
                  />
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Next visit (DD-MM-YYYY shown, stored ISO)</label>
                      <input type="date" className="sohom-input mt-1" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} data-testid="followup-date" />
                    </div>
                    <button onClick={onAddFollowUp} className="sohom-btn-primary mb-0.5" data-testid="save-followup-btn">Save</button>
                  </div>
                  <div className="text-[11px] text-slate-400 pt-1 flex items-start gap-1.5">
                    <Navigation className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>Follow-ups are for calls / admin notes and aren&apos;t counted as travel. Tap <strong className="text-slate-600">&ldquo;I&apos;m here&rdquo;</strong> above when you&apos;re actually on site.</span>
                  </div>
                </div>
              )}
              <div className="space-y-3">
                {followUps.length === 0 ? (
                  <div className="text-sm text-slate-400 italic">No follow-ups yet.</div>
                ) : followUps.map((f) => (
                  <FollowUpRow
                    key={f.id}
                    fu={f}
                    onToggleComplete={() => onToggleFollowUpComplete(f)}
                    onDelete={() => onDeleteFollowUp(f)}
                    onSave={(notes, dateISO) => onSaveEditFollowUp(f, notes, dateISO)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right: photos + visits */}
          <div className="lg:col-span-1 space-y-5">
            <div className="sohom-card p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Photos ({photos.length})</h3>
                {photoSelectMode ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={onDeleteSelectedPhotos}
                      disabled={selectedPhotoIds.size === 0}
                      className="sohom-btn-primary text-xs bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="delete-selected-photos-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete ({selectedPhotoIds.size})
                    </button>
                    <button onClick={exitPhotoSelect} className="sohom-btn-secondary text-xs" data-testid="cancel-photo-select-btn">Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {photos.length > 0 && (
                      <button onClick={() => setPhotoSelectMode(true)} className="sohom-btn-secondary text-xs" data-testid="select-photos-btn">
                        <Check className="w-3.5 h-3.5" /> Select
                      </button>
                    )}
                    <label className={`sohom-btn-primary text-xs ${uploadingPhotos ? "opacity-60 cursor-wait pointer-events-none" : "cursor-pointer"}`} data-testid="upload-photo-btn">
                      {uploadingPhotos ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                      {uploadingPhotos ? `Uploading ${uploadingPhotos.done}/${uploadingPhotos.total}` : "Add"}
                      <input type="file" accept="image/jpeg,image/png,image/webp,image/*" multiple hidden onChange={onAddPhoto} data-testid="upload-photo-input" />
                    </label>
                  </div>
                )}
              </div>
              {photos.length === 0 ? (
                <div className="text-sm text-slate-400 italic py-8 text-center">No photos yet.</div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {photos.map((p) => (
                    <LazyPhoto
                      key={p.id}
                      leadId={id}
                      photo={p}
                      cachedData={photoCache[p.id]}
                      onLoaded={onPhotoLoaded}
                      selectMode={photoSelectMode}
                      selected={selectedPhotoIds.has(p.id)}
                      onClick={() => (photoSelectMode ? togglePhotoSelect(p.id) : setZoomPhoto(p))}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="sohom-card p-5" data-testid="visits-card">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">
                Site visits ({visits.length})
              </h3>
              {visits.length === 0 ? (
                <div className="text-sm text-slate-400 italic py-4 text-center">
                  No check-ins yet. Tap <strong className="text-slate-600">&ldquo;I&apos;m here&rdquo;</strong> above when you arrive on site.
                </div>
              ) : (
                <ul className="space-y-2.5">
                  {visits.map((v) => (
                    <li key={v.id} className="flex items-start gap-2.5 text-sm group" data-testid={`visit-row-${v.id}`}>
                      <div className="mt-1 w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800">{v.created_by_name || "—"}</div>
                        <div className="text-xs text-slate-500">
                          {toDateTime(v.created_at)}
                          {v.accuracy_m ? <> · ±{Math.round(v.accuracy_m)}m</> : null}
                        </div>
                        <a
                          href={`https://www.google.com/maps?q=${v.latitude},${v.longitude}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-sky-600 hover:text-sky-700 inline-flex items-center gap-1 mt-0.5"
                          data-testid={`visit-map-${v.id}`}
                        >
                          <MapPin className="w-3 h-3" /> {v.latitude.toFixed(5)}, {v.longitude.toFixed(5)}
                        </a>
                      </div>
                      {(user?.role === "admin" || v.created_by === user?.id) && (
                        <button
                          onClick={() => onDeleteVisit(v)}
                          className="p-1 rounded text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Delete check-in"
                          data-testid={`visit-delete-${v.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Photo zoom modal */}
      {zoomPhoto && (
        <PhotoZoomModal
          photo={zoomPhoto}
          leadId={id}
          cachedData={photoCache[zoomPhoto.id]}
          onLoaded={onPhotoLoaded}
          onClose={() => setZoomPhoto(null)}
          onSaveCaption={(caption) => onSaveCaption(zoomPhoto.id, caption)}
          onDelete={() => onDeletePhoto(zoomPhoto.id)}
        />
      )}

      {showEdit && (
        <LeadFormDialog
          initial={lead}
          users={users}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); reload(); }}
        />
      )}

      {showWhatsApp && (
        <WhatsAppShareDialog lead={lead} onClose={() => setShowWhatsApp(false)} />
      )}

      {lossPrompt && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="loss-modal">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-5 border-b border-slate-200">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center text-red-600 shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>Mark lead as Lost</h2>
                  <p className="text-xs text-slate-500 mt-0.5">A reason and detailed note are required.</p>
                </div>
              </div>
              <button onClick={() => setLossPrompt(null)} disabled={lossPrompt.saving} className="p-1 text-slate-400 hover:text-slate-700" data-testid="loss-modal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Reason <span className="text-red-500">*</span></label>
                <select
                  className="sohom-input mt-1"
                  value={lossPrompt.reason}
                  onChange={(e) => setLossPrompt((p) => ({ ...p, reason: e.target.value, error: "" }))}
                  disabled={lossPrompt.saving}
                  data-testid="loss-reason-select"
                >
                  <option value="">— Select a reason —</option>
                  {LOSS_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Detailed note <span className="text-red-500">*</span></label>
                <textarea
                  className="sohom-input mt-1"
                  rows={4}
                  placeholder="What happened? Any competitor, price gap, timeline mismatch, etc."
                  value={lossPrompt.note}
                  onChange={(e) => setLossPrompt((p) => ({ ...p, note: e.target.value, error: "" }))}
                  disabled={lossPrompt.saving}
                  data-testid="loss-note-input"
                />
              </div>
              {lossPrompt.error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="loss-modal-error">
                  {lossPrompt.error}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg">
              <button onClick={() => setLossPrompt(null)} disabled={lossPrompt.saving} className="sohom-btn-secondary" data-testid="loss-modal-cancel">Cancel</button>
              <button onClick={submitLossPrompt} disabled={lossPrompt.saving} className="sohom-btn-primary bg-red-600 hover:bg-red-700" data-testid="loss-modal-submit">
                {lossPrompt.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {lossPrompt.saving ? "Saving…" : "Mark as Lost"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}

function LazyPhoto({ leadId, photo, cachedData, onLoaded, onClick, selectMode = false, selected = false }) {
  const ref = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (cachedData) return;
    const el = ref.current;
    if (!el) return;
    let done = false;
    const obs = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting || done) return;
      done = true;
      obs.disconnect();
      setLoading(true);
      try {
        const r = await api.get(`/leads/${leadId}/photos/${photo.id}`);
        if (r.data?.data) onLoaded(photo.id, r.data.data);
      } catch (e) { log.warn("[photo] lazy thumbnail load failed; leaving placeholder", e); }
      finally { setLoading(false); }
    }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [leadId, photo.id, cachedData, onLoaded]);

  return (
    <button
      ref={ref}
      onClick={onClick}
      className={`relative aspect-square rounded-lg overflow-hidden bg-slate-100 border group text-left transition-all ${
        selected ? "border-red-500 ring-2 ring-red-500" : "border-slate-200"
      }`}
      data-testid={`photo-thumb-${photo.id}`}
    >
      {cachedData ? (
        <img src={cachedData} alt={photo.caption || ""} className={`w-full h-full object-cover transition-opacity ${selected ? "opacity-70" : "group-hover:opacity-90"}`} />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-slate-100 animate-pulse">
          <Camera className="w-5 h-5 text-slate-300" />
        </div>
      )}
      {selectMode && (
        <div
          className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center border-2 z-10 ${
            selected ? "bg-red-500 border-red-500 text-white" : "bg-white/80 border-white"
          }`}
          data-testid={`photo-select-${photo.id}`}
        >
          {selected && <Check className="w-4 h-4" />}
        </div>
      )}
      {photo.caption ? (
        <div className="absolute top-1.5 left-1.5 max-w-[calc(100%-12px)]">
          <span className="inline-block bg-sky-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded truncate" data-testid={`photo-caption-badge-${photo.id}`}>
            {photo.caption}
          </span>
        </div>
      ) : (
        <div className="absolute top-1.5 left-1.5">
          <span className="inline-block bg-black/40 text-white/90 text-[10px] px-1.5 py-0.5 rounded">Tap to tag</span>
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] p-1.5 text-left">
        {toDDMMYYYY(photo.added_at)}
      </div>
    </button>
  );
}

function PhotoZoomModal({ photo, leadId, cachedData, onLoaded, onClose, onSaveCaption, onDelete }) {
  const [caption, setCaption] = useState(photo.caption || "");
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(cachedData || photo.data || null);
  const dirty = (caption || "").trim() !== (photo.caption || "").trim();

  useEffect(() => {
    if (data) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/leads/${leadId}/photos/${photo.id}`);
        if (!cancelled && r.data?.data) { setData(r.data.data); onLoaded(photo.id, r.data.data); }
      } catch (e) { log.warn("[photo] zoom image load failed", e); }
    })();
    return () => { cancelled = true; };
  }, [leadId, photo.id, data, onLoaded]);

  const save = async () => {
    setSaving(true);
    try {
      await onSaveCaption((caption || "").trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex flex-col items-center justify-center p-4 sm:p-6" onClick={onClose} data-testid="photo-zoom-modal">
      <button onClick={onClose} className="absolute top-4 right-4 sm:top-6 sm:right-6 text-white p-2 hover:bg-white/10 rounded" data-testid="photo-zoom-close"><X className="w-6 h-6" /></button>
      {data ? (
        <img src={data} alt={photo.caption || ""} className="max-w-full max-h-[60vh] rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
      ) : (
        <div className="max-w-full max-h-[60vh] w-64 h-64 rounded-lg bg-white/10 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <Loader2 className="w-6 h-6 text-white/70 animate-spin" />
        </div>
      )}

      {/* Caption editor */}
      <div className="mt-4 w-full max-w-2xl bg-white rounded-lg p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Tag / caption</div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {PHOTO_TAGS.map((t) => (
            <button
              key={t}
              onClick={() => setCaption(t)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                caption === t
                  ? "bg-sky-600 border-sky-600 text-white"
                  : "bg-white border-slate-300 text-slate-700 hover:border-sky-400 hover:text-sky-700"
              }`}
              data-testid={`photo-tag-${t.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {t}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <input
            className="sohom-input flex-1"
            placeholder="Or type a custom caption…"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            data-testid="photo-caption-input"
          />
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="sohom-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="photo-caption-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={onDelete} className="sohom-btn-secondary text-red-600 hover:bg-red-50" data-testid="delete-photo-btn" title="Delete photo">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        <div className="text-xs text-slate-400 mt-2">
          Uploaded {toDDMMYYYY(photo.added_at)} by {photo.by_user_name || "—"}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{value || <span className="text-slate-400">—</span>}</div>
    </div>
  );
}

function FollowUpRow({ fu, onToggleComplete, onDelete, onSave }) {
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(fu.notes || "");
  // Convert stored ISO → yyyy-mm-dd for <input type="date">
  const [dateStr, setDateStr] = useState(fu.next_visit_date ? new Date(fu.next_visit_date).toISOString().slice(0, 10) : "");
  const [busy, setBusy] = useState(false);

  const startEdit = () => {
    setNotes(fu.notes || "");
    setDateStr(fu.next_visit_date ? new Date(fu.next_visit_date).toISOString().slice(0, 10) : "");
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const save = async () => {
    setBusy(true);
    try {
      await onSave(notes, dateStr ? new Date(dateStr).toISOString() : null);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async () => {
    setBusy(true);
    try { await onToggleComplete(); } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { await onDelete(); } finally { setBusy(false); }
  };

  const borderColor = fu.completed ? "border-emerald-300" : "border-sky-300";
  const bgColor = fu.completed ? "bg-emerald-50/40" : "";

  if (editing) {
    return (
      <div className={`border-l-2 ${borderColor} pl-4 py-2 ${bgColor} rounded-r-md`} data-testid={`followup-edit-${fu.id}`}>
        <textarea
          className="sohom-input"
          rows={2}
          placeholder="Notes…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid={`followup-edit-notes-${fu.id}`}
        />
        <div className="flex gap-2 items-end mt-2">
          <div className="flex-1">
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Next visit</label>
            <input
              type="date"
              className="sohom-input mt-1"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              data-testid={`followup-edit-date-${fu.id}`}
            />
          </div>
          <button onClick={cancelEdit} disabled={busy} className="sohom-btn-secondary mb-0.5" data-testid={`followup-edit-cancel-${fu.id}`}>Cancel</button>
          <button onClick={save} disabled={busy} className="sohom-btn-primary mb-0.5" data-testid={`followup-edit-save-${fu.id}`}>Save</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`border-l-2 ${borderColor} pl-4 py-1.5 ${bgColor} rounded-r-md group`} data-testid={`followup-row-${fu.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className={`text-sm ${fu.completed ? "text-slate-500 line-through decoration-slate-400" : "text-slate-800"}`}>
            {fu.notes || <span className="italic text-slate-400">(no notes)</span>}
          </div>
          <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
            {fu.next_visit_date && (
              <span>Next: <strong>{toDDMMYYYY(fu.next_visit_date)}</strong></span>
            )}
            <span>by {fu.created_by_name || "—"}</span>
            <span>· {toDateTime(fu.created_at)}</span>
            {fu.completed && (
              <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                <Check className="w-3 h-3" />
                Completed {fu.completed_at ? `· ${toDateTime(fu.completed_at)}` : ""}
                {fu.completed_by_name ? ` · by ${fu.completed_by_name}` : ""}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
          <button
            onClick={toggle}
            disabled={busy}
            className={`p-1.5 rounded ${fu.completed ? "text-amber-600 hover:bg-amber-50" : "text-emerald-600 hover:bg-emerald-50"}`}
            title={fu.completed ? "Mark as open" : "Mark as completed"}
            data-testid={`followup-toggle-${fu.id}`}
            aria-label={fu.completed ? "Mark as open" : "Mark as completed"}
          >
            {fu.completed ? <RotateCcw className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          </button>
          <button
            onClick={startEdit}
            disabled={busy}
            className="p-1.5 rounded text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            title="Edit follow-up"
            data-testid={`followup-edit-${fu.id}-btn`}
            aria-label="Edit follow-up"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={remove}
            disabled={busy}
            className="p-1.5 rounded text-red-500 hover:bg-red-50"
            title="Delete follow-up"
            data-testid={`followup-delete-${fu.id}`}
            aria-label="Delete follow-up"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
